import type { JSONContent } from '@tiptap/core';
import type { InvestmentTable } from '@/types';

export type InvestmentColumnKey = 'description' | 'hourlyRate' | 'hours' | 'subtotal';
export type InvestmentColumnWidths = Partial<Record<InvestmentColumnKey, number>>;

export const MIN_INVESTMENT_COLUMN_WIDTH = 72;

const DEFAULT_WIDTHS: Record<InvestmentTable['pricingType'], InvestmentColumnWidths> = {
  hourly: { hourlyRate: 100, hours: 80, subtotal: 120 },
  recurring: { subtotal: 140 },
  fixed: {},
};

export function investmentColumnKeys(
  pricingType: InvestmentTable['pricingType'],
): InvestmentColumnKey[] {
  if (pricingType === 'hourly') {
    return ['description', 'hourlyRate', 'hours', 'subtotal'];
  }
  if (pricingType === 'recurring') return ['description', 'subtotal'];
  return ['description'];
}

function validWidth(value: unknown): number | undefined {
  const width = Number(value);
  if (!Number.isFinite(width) || width < MIN_INVESTMENT_COLUMN_WIDTH) return undefined;
  return Math.round(width);
}

export function normalizedInvestmentColumnWidths(
  pricingType: InvestmentTable['pricingType'],
  widths?: InvestmentColumnWidths,
): InvestmentColumnWidths {
  const allowed = new Set(investmentColumnKeys(pricingType));
  const normalized: InvestmentColumnWidths = { ...DEFAULT_WIDTHS[pricingType] };

  for (const [key, value] of Object.entries(widths ?? {})) {
    if (!allowed.has(key as InvestmentColumnKey)) continue;
    const width = validWidth(value);
    if (width) normalized[key as InvestmentColumnKey] = width;
  }

  return normalized;
}

export function applyInvestmentColumnWidths(
  cells: JSONContent[],
  pricingType: InvestmentTable['pricingType'],
  widths?: InvestmentColumnWidths,
): JSONContent[] {
  const keys = investmentColumnKeys(pricingType);
  const normalized = normalizedInvestmentColumnWidths(pricingType, widths);

  return cells.map((cell, index) => {
    const key = keys[index];
    const width = key ? normalized[key] : undefined;
    if (!width) return cell;
    return {
      ...cell,
      attrs: {
        ...(cell.attrs ?? {}),
        colwidth: [width],
      },
    };
  });
}

/** Lees de actuele Tiptap-kolombreedtes terug uit de eerste complete rij. */
export function readInvestmentColumnWidths(
  table: JSONContent,
  pricingType: InvestmentTable['pricingType'],
): InvestmentColumnWidths {
  const keys = investmentColumnKeys(pricingType);
  const rows = table.content ?? [];
  const sourceRow =
    rows.find((row) => row.type === 'tableRow' && (row.content?.length ?? 0) === keys.length) ??
    rows.find((row) => row.type === 'tableRow');
  const widths: InvestmentColumnWidths = {};

  sourceRow?.content?.forEach((cell, index) => {
    const key = keys[index];
    if (!key) return;
    const raw = (cell.attrs as { colwidth?: unknown } | undefined)?.colwidth;
    const first = Array.isArray(raw) ? raw[0] : undefined;
    const width = validWidth(first);
    if (width) widths[key] = width;
  });

  return normalizedInvestmentColumnWidths(pricingType, widths);
}
